import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import dayjs from 'dayjs';
import { useAuth } from '../../AuthContext';
import { authedFetch } from '../../utils/authedFetch';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

export default function DocumentManager({ documents = [], setDocuments }) {
  const API_BASE = process.env.REACT_APP_API_URL;
  const { session } = useAuth();
  const navigate = useNavigate();

  const [editingDoc, setEditingDoc] = useState(null);
  const [editingSource, setEditingSource] = useState(null);

  const [generateCollapsed, setGenerateCollapsed] = useState(true);
  const [editCollapsed, setEditCollapsed] = useState(true);

  const [showSaveMessage, setShowSaveMessage] = useState(false);
  const [docTypeOther, setDocTypeOther] = useState(false);

  const [gentitle, setGenTitle] = useState('');
  const [doctype, setDocType] = useState('Discharge Summary');
  const [gendate] = useState(dayjs());
  const [gensummary, setGenSummary] = useState('');
  const [generating, setGenerating] = useState(false);
  const [customDocType, setCustomDocType] = useState('');

  const docTypeOptions = [
    'Discharge Summary',
    'Clinic Letter',
    'Operation Note',
    'Ward Round',
    'ED Note',
    'Other (beta)',
  ];

  const isEditing = Boolean(editingDoc?.id);
  const isGeneratedReview = editingSource === 'generated' && !isEditing;

  const handleSave = async () => {
    const title = editingDoc?.title?.trim();
    const content = cleanDocumentHtml(editingDoc?.content || '');
    const type = editingDoc?.type?.trim();
    const date = editingDoc?.date || dayjs();
    const time_offset = editingDoc?.time_offset ?? 0;
    const time_offset_unit = editingDoc?.time_offset_unit || 'hours';

    if (!title || !content) {
      alert('Both title and content are required.');
      return;
    }

    const newDoc = {
      id: isEditing ? editingDoc.id : Date.now().toString(),
      title,
      type,
      content,
      date: dayjs(date).toISOString(),
      time_offset,
      time_offset_unit,
    };

    const updated = isEditing
      ? documents.map((doc) => (doc.id === editingDoc.id ? newDoc : doc))
      : [...documents, newDoc];

    setDocuments(updated);
    setEditingDoc(null);
    setEditingSource(null);
    setEditCollapsed(true);
    setGenerateCollapsed(true);

    setShowSaveMessage(true);
    setTimeout(() => setShowSaveMessage(false), 1500);
  };

  const handleExpandNew = () => {
    const opening = editCollapsed;

    setEditCollapsed(!editCollapsed);
    setGenerateCollapsed(true);
    setEditingSource('manual');

    if (opening) {
      setEditingDoc({
        title: '',
        type: '',
        content: '',
        date: dayjs(),
        time_offset: 0,
        time_offset_unit: 'hours',
      });
    } else {
      setEditingDoc(null);
      setEditingSource(null);
    }
  };

  const handleDelete = (id) => {
    setDocuments(documents.filter((doc) => doc.id !== id));

    if (editingDoc?.id === id) {
      setEditingDoc(null);
      setEditingSource(null);
      setEditCollapsed(true);
    }
  };

  const handleEditClick = (doc) => {
    setEditingDoc({ ...doc });
    setEditingSource('manual');
    setEditCollapsed(false);
    setGenerateCollapsed(true);
  };

  const handleCancelEdit = () => {
    setEditingDoc(null);
    setEditingSource(null);
    setEditCollapsed(true);
  };

  const handleChangeType = (value) => {
    setDocType(value);
    setDocTypeOther(value === 'Other (beta)');
  };

  function cleanDocumentHtml(html = '') {
    return html
      // remove markdown code fences
      .replace(/```html/gi, '')
      .replace(/```/g, '')

      // remove empty Quill paragraphs
      .replace(/<p><br\s*\/?><\/p>/gi, '')
      .replace(/<p>\s*<\/p>/gi, '')

      // remove repeated blank lines
      .replace(/\n{2,}/g, '\n')

      // remove excess whitespace between tags
      .replace(/>\s+</g, '><')

      // trim
      .trim();
  };

  const handleGenerate = async () => {
    if (!gentitle || !doctype || !gensummary) return;

    const finalDocType =
      doctype === 'Other (beta)'
        ? customDocType.trim() || doctype
        : doctype;

    const payload = {
      gentitle,
      doctype: finalDocType,
      gendate,
      gensummary,
    };

    setGenerating(true);

    try {
      const res = await authedFetch({
        accessToken: session?.access_token,
        apiBase: API_BASE,
        path: '/api/generatedoc',
        navigate,
        options: {
          method: 'POST',
          body: JSON.stringify(payload),
        },
      });

      const data = await res.json();
      const content = cleanDocumentHtml(data.response || '');

      setEditingDoc({
        title: gentitle,
        type: finalDocType,
        content,
        date: gendate,
        time_offset: 0,
        time_offset_unit: 'hours',
      });

      setEditingSource('generated');
      setEditCollapsed(false);
      setGenerateCollapsed(true);
    } catch (err) {
      console.error('Generation failed', err);
    } finally {
      setGenerating(false);
    }
  };

  const SectionHeader = ({ icon, title, subtitle, onClick, collapsed }) => (
    <Box
      onClick={onClick}
      sx={{
        px: 2,
        py: 1.25,
        bgcolor: '#dbeafe',
        borderBottom: '1px solid #60A5FA',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon}

        <Box>
          <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>
      </Box>

      {typeof collapsed === 'boolean' && (
        <Typography variant="body2" color="text.secondary">
          {collapsed ? 'Expand' : 'Collapse'}
        </Typography>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {showSaveMessage && (
        <Alert severity="success">Document saved successfully.</Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          border: '1px solid #60A5FA',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <SectionHeader
          icon={<AutoAwesomeIcon />}
          title="Generate document"
          subtitle="Draft a document from a short summary."
          onClick={() => {
            setGenerateCollapsed(!generateCollapsed);

            if (generateCollapsed) {
              setEditCollapsed(true);
            }
          }}
          collapsed={generateCollapsed}
        />

        {!generateCollapsed && (
          <Box sx={{ p: 2 }}>
            <Stack spacing={2}>
              <TextField
                label="Document title"
                value={gentitle}
                onChange={(e) => setGenTitle(e.target.value)}
                fullWidth
              />

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', lg: '1.4fr 1fr' },
                  gap: 2,
                }}
              >
                <TextField
                  multiline
                  minRows={4}
                  label="Document summary"
                  placeholder="Provide a brief summary of the document and include any important details."
                  value={gensummary}
                  onChange={(e) => setGenSummary(e.target.value)}
                  fullWidth
                />

                <Stack spacing={1.5}>
                  <Autocomplete
                    options={docTypeOptions}
                    value={doctype}
                    onChange={(event, newValue) => handleChangeType(newValue)}
                    renderInput={(params) => (
                      <TextField {...params} label="Document type" />
                    )}
                  />

                  {docTypeOther && (
                    <TextField
                      label="Custom document type"
                      value={customDocType}
                      onChange={(e) => setCustomDocType(e.target.value)}
                      fullWidth
                    />
                  )}

                  <Button
                    variant="contained"
                    onClick={handleGenerate}
                    sx={{ textTransform: 'none', minWidth: 180 }}
                    disabled={generating}
                  >
                    {generating ? 'Generating...' : 'Generate document'}
                  </Button>
                </Stack>
              </Box>

              {generating && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress />
                </Box>
              )}
            </Stack>
          </Box>
        )}
      </Paper>

      <Paper
        elevation={0}
        sx={{
          border: '1px solid #60A5FA',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <SectionHeader
          icon={<NoteAddIcon />}
          title={
            isEditing
              ? 'Edit document'
              : isGeneratedReview
                ? 'Review generated document'
                : 'Create document manually'
          }
          subtitle={
            isGeneratedReview
              ? 'Review, edit, set the time offset, then save.'
              : 'Write and save a document directly.'
          }
          onClick={() => {
            if (!isEditing && !isGeneratedReview) {
              handleExpandNew();
            }
          }}
          collapsed={!isEditing && !isGeneratedReview ? editCollapsed : false}
        />

        {!editCollapsed && editingDoc && (
          <Box sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: 2,
                }}
              >
                <TextField
                  label="Title"
                  value={editingDoc?.title || ''}
                  onChange={(e) =>
                    setEditingDoc((prev) => ({
                      ...prev,
                      title: e.target.value,
                    }))
                  }
                  fullWidth
                />

                <TextField
                  label="Document type"
                  value={editingDoc?.type || ''}
                  onChange={(e) =>
                    setEditingDoc((prev) => ({
                      ...prev,
                      type: e.target.value,
                    }))
                  }
                  fullWidth
                />
              </Box>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                <TextField
                  label="Time offset"
                  type="number"
                  value={editingDoc?.time_offset ?? 0}
                  onChange={(e) =>
                    setEditingDoc((prev) => ({
                      ...prev,
                      time_offset:
                        e.target.value === '' ? '' : Number(e.target.value),
                    }))
                  }
                  sx={{ width: 180 }}
                />

                <TextField
                  select
                  label="Offset unit"
                  value={editingDoc?.time_offset_unit ?? 'hours'}
                  onChange={(e) =>
                    setEditingDoc((prev) => ({
                      ...prev,
                      time_offset_unit: e.target.value,
                    }))
                  }
                  sx={{ width: 180 }}
                >
                  <MenuItem value="minutes">Minutes</MenuItem>
                  <MenuItem value="hours">Hours</MenuItem>
                  <MenuItem value="days">Days</MenuItem>
                  <MenuItem value="weeks">Weeks</MenuItem>
                  <MenuItem value="months">Months</MenuItem>
                </TextField>
              </Stack>

              <Box
                sx={{
                  '& .ql-container': {
                    minHeight: 220,
                  },
                  '& .ql-editor': {
                    minHeight: 180,
                  },
                }}
              >
                <ReactQuill
                  theme="snow"
                  value={editingDoc?.content || ''}
                  onChange={(content) =>
                    setEditingDoc((prev) => ({ ...prev, content }))
                  }
                />
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5 }}>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  sx={{ textTransform: 'none' }}
                >
                  {isEditing ? 'Update document' : 'Save document'}
                </Button>

                <Button onClick={handleCancelEdit} sx={{ textTransform: 'none' }}>
                  Cancel
                </Button>
              </Box>
            </Stack>
          </Box>
        )}
      </Paper>

      <Paper
        elevation={0}
        sx={{
          border: '1px solid #60A5FA',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <SectionHeader
          icon={<DescriptionOutlinedIcon />}
          title="Saved documents"
          subtitle="View, edit, and remove saved records."
        />

        <Box sx={{ p: 2 }}>
          {documents.length === 0 ? (
            <Typography color="text.secondary">
              No documents saved yet.
            </Typography>
          ) : (
            <Stack spacing={1.25}>
              {documents.map((doc) => (
                <Paper
                  key={doc.id}
                  elevation={0}
                  sx={{
                    border: '1px solid #d1d5db',
                    borderRadius: 2,
                    px: 1.5,
                    py: 1.25,
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={1.5}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                        {doc.title}
                      </Typography>

                      <Typography sx={{ fontWeight: 400, fontSize: 12 }}>
                        {`${doc.time_offset ?? 0} ${doc.time_offset_unit || 'hours'} ago`}
                      </Typography>

                      <Stack
                        direction="row"
                        spacing={0.75}
                        useFlexGap
                        flexWrap="wrap"
                        sx={{ mt: 0.75 }}
                      >
                        {doc.type && <Chip size="small" label={doc.type} />}
                      </Stack>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton onClick={() => handleEditClick(doc)}>
                        <EditIcon />
                      </IconButton>

                      <IconButton onClick={() => handleDelete(doc.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </Box>
      </Paper>
    </Box>
  );
}